import { Box, Button, Typography } from "@outschool/backpack";
import { CurrencyCode, DeliveryType } from "@outschool/gql-backend-generated";
import {
  Trans,
  useCurrencyLocalization,
  useTranslation
} from "@outschool/localization";
import { savedClassesPath, searchPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  BrowseGrid,
  IncrementalLoader,
  ListingCarousel,
  PromotionBanner,
  VisitHistoryListingsCarousel,
  useSubjectKeywordLinksData
} from "@outschool/ui-components-website";
import {
  Container,
  Loading,
  PageWidthSection
} from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { LearnerInterestTopicsQuery } from "../../../shared/generated/graphql";
import { useReturnToUSD } from "../../hooks/Currency";
import { useLearnerInterestTopics } from "../../hooks/Topics";
import {
  isOrganizationAdminWhoCanEnroll,
  searchDefaults
} from "../../lib/Users";
// @ts-ignore
import ClassRequestForm from "../ClassRequestForm";
import { LowEsaCreditBanner } from "../esaMemberships/LowEsaCreditBanner";
import Link from "../Link";
import StickyHeader from "../parentDashboard/tabs/StickyHeader";
import FeaturedClassCarousel, {
  CAROUSEL_PLACEHOLDER_HEIGHT
} from "../search/FeaturedClassCarousel";
import SearchListingsCarousel from "../SearchListingsCarousel";
import EnrolledLearnerSuccessBanner from "./EnrolledLearnerSuccessBanner";
import HobbyTopics from "./HobbyTopics";
import { AddLearnerBanner } from "./learnerCards/AddLearnerBanner";
import { LoggedInHomeMyClassesList } from "./LoggedInHomeMyClassesList";
import { WalletCTABanner } from "./walletCTA/WalletCTABanner";

type DateBasedTopicCarousel = {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  topics: string[];
  q?: string;
  deliveryTypes?: DeliveryType[];
  title: string;
  trackingLabel: string;
};

export default function ParentDashboardDesktop({ children }: $TSFixMe) {
  const { t } = useTranslation("client\\components\\loggedInHome\\Dashboard");
  const { currentUser, currentUserIsLoading } = useSession();
  const { data, loading: learnersWithInterestsLoading } =
    useLearnerInterestTopics();
  const isMobile = useIsMobile();

  const learnersWithInterests = data?.currentUser?.children;

  const loading = currentUserIsLoading || learnersWithInterestsLoading;

  const child = loading ? (
    <Loading />
  ) : isOrganizationAdminWhoCanEnroll(currentUser) ? (
    <Trans t={t}>
      <Box
        sx={(theme: $TSFixMe) => ({
          maxWidth: "750px",
          position: "relative",
          width: "100%",
          marginX: "auto",
          boxSizing: "border-box",
          paddingX: "1em",

          [theme.breakpoints.up("md")]: {
            paddingX: "2em"
          },

          paddingY: "2em",
          textAlign: "center"
        })}
      >
        You can always reach us by email at{" "}
        <a href="mailto:support@outschool.com">support@outschool.com</a>
      </Box>
    </Trans>
  ) : (
    <FeaturedClasses learnersWithInterests={learnersWithInterests} />
  );

  return (
    <>
      <StickyHeader />
      <AddLearnerBanner />
      <EnrolledLearnerSuccessBanner />
      {currentUser?.hasEsaMembership && <LowEsaCreditBanner />}
      {
        // Fix for error thrown on callback from apple sign up P. Albert Jun 20 2022
        !currentUser ? null : (
          <Container size="medium">
            <LoggedInHomeMyClassesList />
          </Container>
        )
      }

      {children && (
        <Box
          sx={{
            marginY: isMobile ? "1em" : "2em"
          }}
        >
          {children}
        </Box>
      )}

      <WalletCTABanner />

      {child}
    </>
  );
}

const aggregatedUpsellQuery = gql`
  query AggregatedUpsellQuery(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      aggregatedUpsell {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const favoriteListingsQuery = gql`
  query FavouriteListings($skipRating: Boolean!, $withRecentReviews: Boolean!) {
    featured {
      favoriteListings {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const startingSoonQuery = gql`
  query StartingSoon($skipRating: Boolean!, $withRecentReviews: Boolean!) {
    featured {
      startingSoon {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const personalizedRecommendationsQuery = gql`
  query PersonalizedRecommendations(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      personalizedRecommendations {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const classesUnderTwentyDollarsQuery = gql`
  query ClassesUnderTwentyDollars(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      underTwentyDollars {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const classesUnderSomeUSDQuery = gql`
  query ClassesUnderSomeUSD(
    $skipRating: Boolean!
    $amountCents: Int!
    $withRecentReviews: Boolean!
  ) {
    featured {
      underSomeCents(amountCents: $amountCents) {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const backToOutschoolQuery = gql`
  query BackToOutschoolQuery(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      backToOutschool {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const personalizedPopularListingsQuery = gql`
  query PersonalizedPopularListings(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      personalizedPopularListings {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

const popularTutorListingsQuery = gql`
  query PopularTutorListings(
    $skipRating: Boolean!
    $withRecentReviews: Boolean!
  ) {
    featured {
      popularTutorListings {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

export const InterestTopicsListingsQuery = gql`
  query InterestTopicsListingsQuery(
    $skipRating: Boolean!
    $topicUids: [ID!]!
    $learnerAge: Int!
    $withRecentReviews: Boolean!
  ) {
    featured {
      interestTopics(topicUids: $topicUids, learnerAge: $learnerAge) {
        ...ListingCarousel_listing
      }
    }
  }
  ${ListingCarousel.fragments.listing}
`;

function FeaturedClasses({
  learnersWithInterests
}: {
  // @ts-ignore TS(2339): Property 'children' does not exist on type '{ __ty... Remove this comment to see the full error message
  learnersWithInterests: LearnerInterestTopicsQuery["currentUser"]["children"];
}) {
  const skipRatingVariables = {
    skipRating: false
  };
  const { t } = useTranslation("client\\components\\loggedInHome\\Dashboard");
  const { currentUser } = useSession();
  const { currencyCode } = useCurrencyLocalization();
  const convertToUSDCents = useReturnToUSD();
  const showGBPCarousel =
    currencyCode && currencyCode.toString() === CurrencyCode.Gbp;

  /*
    How far from the component to start the query
    this must be less than carouselMinHeight
    or else useOnScreen seems to break
  */
  const rootMargin = `${Math.floor(CAROUSEL_PLACEHOLDER_HEIGHT / 2)}px 0px`;
  // NOTE: Watch out for apostrophe in the title, display should use curly apostrophe, but we should use straight apostrophe for the query
  const dateBasedTopicCarousels: DateBasedTopicCarousel[] = [
    {
      start: dayjs("02-01", "MM-DD"),
      end: dayjs("02-15", "MM-DD"),
      topics: ["Valentines", "Valentine's"],
      title: t("Valentine’s Day"),
      q: "Valentine's Day",
      trackingLabel: "loggedInValentines"
    },
    {
      start: dayjs("01-31-2022", "MM-DD-YYYY"),
      end: dayjs("02-15-2022", "MM-DD-YYYY"),
      topics: ["Chinese New Year", "Lunar New Year"],
      title: t("Chinese/Lunar New Year"),
      q: "Chinese New Year",
      trackingLabel: "loggedInChineseNewYear"
    },
    {
      start: dayjs("12-01", "MM-DD"),
      end: dayjs("12-31", "MM-DD"),
      topics: ["holiday", "winter", "baking", "Christmas", "reindeer", "santa"],
      title: t("Holiday cheer ☃️"),
      q: "Christmas",
      trackingLabel: "loggedInHomeHolidayCheer"
    },
    {
      start: dayjs("02-01", "MM-DD"),
      end: dayjs("03-01", "MM-DD").subtract(1, "day"), // Leap year handling
      topics: [
        "Black History",
        "Decolonization",
        "African American History",
        "Diaspora",
        "Civil Rights"
      ],
      q: "Black History",
      title: t("Celebrate Black History Month️"),
      trackingLabel: "loggedInBlackHistoryMonth"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-31", "MM-DD"),
      topics: [
        "Women's History",
        "feminism",
        "suffrage",
        "ruth bader ginsburg",
        "lucille ball"
      ],
      q: "Women's History",
      title: t("Women’s History Month"),
      trackingLabel: "loggedInWomensHistoryMonth"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-18", "MM-DD"),
      topics: ["st. patrick's"],
      q: "St. Patrick's",
      title: t("St. Patrick’s Day"),
      trackingLabel: "loggedInStPatrickDay"
    },
    {
      start: dayjs("03-01", "MM-DD"),
      end: dayjs("03-31", "MM-DD"),
      topics: ["Spring Break"],
      q: "Spring Break",
      title: t("Spring Break"),
      trackingLabel: "loggedInSpringBreakCamps"
    },
    {
      start: dayjs("04-06-2021", "MM-DD"),
      end: dayjs("04-30-2021", "MM-DD"),
      topics: [
        "earth day",
        "earth month",
        "earth science",
        "environmental science",
        "endangered",
        "climate",
        "conservation",
        "nature",
        "animals",
        "Earth Sciences",
        "Environmentalism"
      ],
      q: "earth day",
      title: t("Celebrate Earth Month"),
      trackingLabel: "loggedInEarthMonth"
    },
    {
      start: dayjs("06-01", "MM-DD"),
      end: dayjs("06-30", "MM-DD"),
      topics: [
        "LGBT",
        "LGBTQ",
        "LGBTQIA",
        "queer",
        "transgender",
        "trans",
        "stonewall"
      ],
      q: "LGTBQ",
      title: t("Celebrate Pride Month"),
      trackingLabel: "loggedInCelebratePrideMonth"
    },
    {
      start: dayjs("08-01", "MM-DD"),
      end: dayjs("08-31", "MM-DD"),
      topics: [],
      q: "Back to school",
      title: t("Back to Outschool"),
      trackingLabel: "loggedInBackToOutschool"
    },
    {
      start: dayjs("09-01", "MM-DD"),
      end: dayjs("09-30", "MM-DD"),
      topics: [
        "Spain",
        '"Hispanic Culture"',
        '"Hispanic Heritage Month"',
        '"Latin America"',
        '"Puerto Rico"',
        '"Spanish Culture"'
      ],
      title: t("Hispanic Heritage Month"),
      trackingLabel: "loggedInHispanicHeritageMonth"
    },
    {
      start: dayjs("10-01", "MM-DD"),
      end: dayjs("10-31", "MM-DD"),
      topics: ["Halloween"],
      q: "Halloween",
      title: t("Halloween fun"),
      trackingLabel: "loggedInHalloweenFun"
    },
    {
      start: dayjs("11-01", "MM-DD"),
      end: dayjs("11-30", "MM-DD"),
      topics: ["Native American", "First nations", "Aboriginal"],
      q: "Native American",
      title: t("National Native American Heritage Month"),
      trackingLabel: "loggedInNationalNativeAmericanHeritageMonth"
    },
    {
      start: dayjs("06-01", "MM-DD"),
      end: dayjs("07-06", "MM-DD"),
      topics: [
        "Juneteenth",
        "Black History",
        "Black Independence Day",
        "Emancipation",
        "Decolonized",
        "Liberation Day"
      ],
      q: "Black History",
      title: t("Classes on social justice, race, and history"),
      trackingLabel: "loggedInJuneteenthClasses"
    }
  ];

  const filteredDateBasedTopicCarousels = dateBasedTopicCarousels.filter(
    ({ start, end }) => dayjs().isBetween(start, end, "day", "[]")
  );

  let rowNum = 0;

  const subjectKeywordLinksData = useSubjectKeywordLinksData();

  return (
    <IncrementalLoader rootMargin={rootMargin}>
      <VisitHistoryListingsCarousel carouselRowNum={rowNum++} />
      {learnersWithInterests &&
        learnersWithInterests.map(
          (learner: $TSFixMe) =>
            learner?.interestTopics?.topics?.length > 0 && (
              <FeaturedClassCarousel
                key={learner.uid}
                query={InterestTopicsListingsQuery}
                variables={{
                  ...skipRatingVariables,
                  withRecentReviews: false,
                  topicUids: learner.interestTopics.topics.map(
                    (topic: $TSFixMe) => topic.uid
                  ),
                  learnerAge: learner.age
                }}
                path="featured.interestTopics"
                title={t("Based on {{learnerName}}’s interests", {
                  learnerName: learner.name
                })}
                trackingLabel="loggedInHomeLearnerInterestTopics"
                carouselRowNum={rowNum++}
              />
            )
        )}
      <HobbyTopics />
      <FeaturedClassCarousel
        query={aggregatedUpsellQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.aggregatedUpsell"
        title={t("Recommended from your teachers")}
        trackingLabel="loggedInHomeAggregatedUpsell"
        carouselRowNum={rowNum++}
      />
      <FeaturedClassCarousel
        query={favoriteListingsQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.favoriteListings"
        title={t("Coming up from your favorites")}
        trackingLabel="loggedInHomeUpcomingFavorites"
        carouselRowNum={rowNum++}
      >
        <Box
          sx={{
            textAlign: "center",
            marginTop: "1em",
            marginBottom: "2em"
          }}
        >
          <Button component={Link} to={savedClassesPath()}>
            {t("More Favorites")}
          </Button>
        </Box>
      </FeaturedClassCarousel>
      <FeaturedClassCarousel
        query={popularTutorListingsQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.popularTutorListings"
        title={t("Popular tutors available now")}
        trackingLabel="loggedInHomePopularTutors"
        carouselRowNum={rowNum++}
      />
      <FeaturedClassCarousel
        query={personalizedRecommendationsQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.personalizedRecommendations"
        title={t("You may also like")}
        trackingLabel="loggedInHomeClassesYouMayAlsoLike"
        carouselRowNum={rowNum++}
        batchKey="Compute Carousels"
      />
      <FeaturedClassCarousel
        query={startingSoonQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.startingSoon"
        title={t("Starting within 24 hours")}
        trackingLabel="loggedInHomeStartingSoon"
        carouselRowNum={rowNum++}
      />
      <FeaturedClassCarousel
        query={backToOutschoolQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.backToOutschool"
        title={t("Weekly enrichment & clubs")}
        trackingLabel="loggedInHomeBackToOutschool"
        carouselRowNum={rowNum++}
      >
        <Box
          sx={{
            textAlign: "center",
            marginTop: "1em",
            marginBottom: "2em"
          }}
        >
          <TrackedButton
            component={Link}
            to={searchPath({
              // @ts-ignore TS(2345): Argument of type '{ __typename?: "User" | undefine... Remove this comment to see the full error message
              ...searchDefaults(currentUser),
              delivery: "Ongoing class"
            })}
            trackingUniqueId={"ongoing_class_cta"}
          >
            {t("Browse Weekly Clubs")}
          </TrackedButton>
        </Box>
      </FeaturedClassCarousel>
      {filteredDateBasedTopicCarousels.map(
        ({ topics, deliveryTypes, title, trackingLabel, q }) => {
          return (
            <SearchListingsCarousel
              key={trackingLabel}
              searchFilters={{
                topics,
                delivery: deliveryTypes,
                q
              }}
              title={title}
              trackingLabel={trackingLabel}
              carouselRowNum={rowNum++}
            />
          );
        }
      )}
      {showGBPCarousel && (
        <FeaturedClassCarousel
          query={classesUnderSomeUSDQuery}
          variables={{
            skipRating: false,
            amountCents: convertToUSDCents(1500)
          }}
          path="featured.underSomeCents"
          title={t("Classes under £15")}
          trackingLabel={"loggedInHomeUnderFifteenPounds"}
          carouselRowNum={rowNum++}
        />
      )}
      {!showGBPCarousel && (
        <FeaturedClassCarousel
          query={classesUnderTwentyDollarsQuery}
          variables={{
            ...skipRatingVariables,
            withRecentReviews: false
          }}
          path="featured.underTwentyDollars"
          title={t("Classes under $20")}
          trackingLabel={"loggedInHomeUnderTwentyDollars"}
          carouselRowNum={rowNum++}
        />
      )}

      <FeaturedClassCarousel
        query={personalizedPopularListingsQuery}
        variables={{
          ...skipRatingVariables,
          withRecentReviews: false
        }}
        path="featured.personalizedPopularListings"
        title={t("Popular classes")}
        trackingLabel="loggedInHomePersonalizedPopularListings"
        carouselRowNum={rowNum++}
      />

      <Container
        fullBleed
        sx={{
          paddingBottom: "small",
          paddingTop: "large"
        }}
      >
        <PromotionBanner />
      </Container>
      <Container
        sx={{
          backgroundColor: "white",
          paddingBottom: "small",
          paddingTop: "large"
        }}
      >
        <BrowseGrid
          title={t("Browse classes & tutoring")}
          showTopBoxes={true}
          subjectKeywordLinksData={subjectKeywordLinksData}
        />
      </Container>
      <RequestAClass />
    </IncrementalLoader>
  );
}

function RequestAClass() {
  const { t } = useTranslation("client\\components\\loggedInHome\\Dashboard");
  const { isLoggedIn } = useSession();
  return (
    <PageWidthSection
      sx={{
        backgroundColor: "rgba(43, 167, 206, 0.1)",
        paddingBottom: "4em",
        marginBottom: "-2em"
      }}
    >
      <Box
        sx={{
          textAlign: "center"
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{
            marginBottom: "0.5em"
          }}
        >
          {t("Interested in something in particular?")}
        </Typography>
        <ClassRequestForm
          isLoggedIn={isLoggedIn}
          renderButton={(open: $TSFixMe) => (
            <Button variant="contained" onClick={open}>
              {t("Request a Topic")}
            </Button>
          )}
        />
      </Box>
    </PageWidthSection>
  );
}
